import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useWebSocket, WebSocketMessage } from '../../../hooks/useWebSocket';
import styles from '../TradeFlowTrackingTab.module.css';

interface TradeFlowEvent {
    stage: string;
    stage_label: string;
    event_type: string;
    symbol: string;
    timestamp: string;
    filter_name?: string;
    filter_category?: string;
    passed?: boolean;
    score?: number;
    threshold?: number;
    detail?: string;
    direction?: string;
    recommendation_id?: string;
    model_version?: string;
    actual_value?: number;
}

const MAX_EVENTS = 300;
const STAGE_COLORS: Record<string, string> = {
    Fetch: '#58a6ff',
    Genesis: '#bc8cff',
    Pipeline: '#f0883e',
    XGBoost: '#3fb950',
    LightGBM: '#79c0ff',
    Scoring: '#d2a8ff',
    'Smart Pool': '#ffa657',
    'Active Pool': '#7ee787',
    'Live Gate': '#ff7b72',
    Technical: '#f778ba',
    Sniper: '#db6d28',
    Execution: '#a5d6ff',
};

function getStageColor(stageLabel: string): string {
    return STAGE_COLORS[stageLabel] || '#8b949e';
}

function formatTimestamp(iso: string): string {
    try {
        const d = new Date(iso);
        return d.toLocaleTimeString('en-US', { hour12: false });
    } catch {
        return iso;
    }
}

const LiveStreamPanel: React.FC = () => {
    const [events, setEvents] = useState<TradeFlowEvent[]>([]);
    const [paused, setPaused] = useState(false);
    const [autoScroll, setAutoScroll] = useState(true);
    const scrollRef = useRef<HTMLDivElement>(null);
    const pausedBufferRef = useRef<TradeFlowEvent[]>([]);

    const getWsUrl = useCallback((): string => {
        if (import.meta.env.DEV) {
            return 'ws://localhost:8000/api/admin/trade-flow/ws';
        }
        const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
        return `${proto}://${window.location.host}/api/admin/trade-flow/ws`;
    }, []);

    const handleMessage = useCallback((msg: WebSocketMessage) => {
        if (msg.type === 'update' && msg.channel?.includes('trade_flow')) {
            const event = msg.data as TradeFlowEvent;
            if (pausedBufferRef.current) {
                pausedBufferRef.current.push(event);
            } else {
                setEvents((prev) => {
                    const next = [...prev, event];
                    if (next.length > MAX_EVENTS) {
                        return next.slice(next.length - MAX_EVENTS);
                    }
                    return next;
                });
            }
        }
    }, []);

    useEffect(() => {
        pausedBufferRef.current = paused ? [] : pausedBufferRef.current;
    }, [paused]);

    useEffect(() => {
        if (scrollRef.current && autoScroll && !paused) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [events, autoScroll, paused]);

    const { isConnected } = useWebSocket({
        url: getWsUrl(),
        onMessage: handleMessage,
        enabled: true,
        maxReconnectAttempts: 5,
    });

    const handleClear = () => {
        setEvents([]);
        pausedBufferRef.current = [];
    };

    const handleResume = () => {
        setPaused(false);
        if (pausedBufferRef.current.length > 0) {
            setEvents((prev) => {
                const next = [...prev, ...pausedBufferRef.current];
                pausedBufferRef.current = [];
                if (next.length > MAX_EVENTS) {
                    return next.slice(next.length - MAX_EVENTS);
                }
                return next;
            });
        }
    };

    return (
        <div className={styles.liveStreamPanel}>
            <div className={styles.liveStreamHeader}>
                <div className={styles.liveStreamTitle}>
                    <span
                        className={styles.liveDot}
                        style={{
                            background: isConnected ? '#3fb950' : '#f85149',
                        }}
                    />
                    <h3>Live Pipeline Stream</h3>
                    <span className={styles.liveBadge}>
                        {isConnected ? 'LIVE' : 'DISCONNECTED'}
                    </span>
                    <span className={styles.eventCount}>
                        {events.length} events
                    </span>
                </div>
                <div className={styles.liveStreamActions}>
                    <button
                        className={styles.liveStreamBtn}
                        onClick={() => {
                            if (paused) {
                                handleResume();
                            } else {
                                setPaused(true);
                            }
                        }}
                    >
                        {paused ? 'Resume' : 'Pause'}
                    </button>
                    <button
                        className={styles.liveStreamBtn}
                        onClick={() => setAutoScroll(!autoScroll)}
                    >
                        {autoScroll ? 'Auto-Scroll: ON' : 'Auto-Scroll: OFF'}
                    </button>
                    <button className={styles.liveStreamBtn} onClick={handleClear}>
                        Clear
                    </button>
                </div>
            </div>
            <div className={styles.liveStreamBody} ref={scrollRef}>
                {events.length === 0 && (
                    <div className={styles.liveStreamEmpty}>
                        {isConnected
                            ? 'Waiting for events... Pipeline activity will appear here in real-time.'
                            : 'Connecting to live stream...'}
                    </div>
                )}
                {events.map((evt, idx) => (
                    <div
                        key={`${evt.timestamp}-${evt.stage}-${evt.symbol}-${idx}`}
                        className={styles.liveStreamRow}
                    >
                        <span className={styles.liveTime}>
                            {formatTimestamp(evt.timestamp)}
                        </span>
                        <span
                            className={styles.liveStageTag}
                            style={{
                                background: getStageColor(evt.stage_label),
                                color: '#fff',
                            }}
                        >
                            {evt.stage_label}
                        </span>
                        <span className={styles.liveSymbol}>{evt.symbol}</span>
                        {evt.filter_name && (
                            <span className={styles.liveFilter}>
                                {evt.filter_name}
                            </span>
                        )}
                        {evt.passed !== undefined && (
                            <span
                                className={
                                    evt.passed
                                        ? styles.livePass
                                        : styles.liveFail
                                }
                            >
                                {evt.passed ? 'PASS' : 'FAIL'}
                            </span>
                        )}
                        {evt.score !== undefined && evt.score !== null && (
                            <span className={styles.liveScore}>
                                {evt.score.toFixed(4)}
                            </span>
                        )}
                        {evt.detail && (
                            <span className={styles.liveDetail}>
                                {evt.detail.length > 80
                                    ? evt.detail.slice(0, 80) + '...'
                                    : evt.detail}
                            </span>
                        )}
                    </div>
                ))}
                {paused && (
                    <div className={styles.livePausedBanner}>
                        PAUSED — Click Resume to continue
                    </div>
                )}
            </div>
        </div>
    );
};

export default LiveStreamPanel;
